import { useState, useRef, useCallback } from 'react';
import { sanitizeSpeechInput } from '../utils/sanitize';

export const useSpeechToText = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const startListening = useCallback(() => {
    try {
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        setError('Speech recognition not supported in this browser');
        return;
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      
      if (!SpeechRecognition) {
        setError('Speech recognition API not available');
        return;
      }
      
      const recognition = new SpeechRecognition();
      
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        console.log('Speech recognition started');
        setIsListening(true);
        setError(null);
        setTranscript('');
      };

      recognition.onresult = (event) => {
        try {
          if (!event.results || event.results.length === 0) {
            console.warn('No speech recognition results received');
            return;
          }
          
          const result = event.results[0]?.[0]?.transcript;
          if (!result) {
            console.warn('Empty speech recognition result');
            return;
          }
          
          const sanitizedResult = sanitizeSpeechInput(result);
          if (sanitizedResult !== result) {
            console.warn('Speech input was sanitized for security');
          }
          
          setTranscript(sanitizedResult);
        } catch (err) {
          console.error('Error processing speech result:', err);
          setError('Failed to process speech result');
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error, event);
        const errorMessage = getErrorMessage(event.error);
        setError(errorMessage);
        setIsListening(false);
      };

      recognition.onend = () => {
        console.log('Speech recognition ended');
        setIsListening(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      console.error('Failed to initialize speech recognition:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize speech recognition';
      setError(errorMessage);
      setIsListening(false);
    }
  }, []);

  const stopListening = useCallback(() => {
    try {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
    } catch (err) {
      console.error('Failed to stop speech recognition:', err);
      setError('Failed to stop speech recognition');
    }
  }, []);

  const getErrorMessage = (errorType: string): string => {
    switch (errorType) {
      case 'no-speech':
        return 'No speech detected. Please try again.';
      case 'audio-capture':
        return 'Microphone access denied or unavailable.';
      case 'not-allowed':
        return 'Microphone permission denied.';
      case 'network':
        return 'Network error occurred during speech recognition.';
      case 'aborted':
        return 'Speech recognition was aborted.';
      default:
        return `Speech recognition error: ${errorType}`;
    }
  };

  return {
    isListening,
    transcript,
    error,
    startListening,
    stopListening
  };
};